import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AlertTriangle, Loader2, RefreshCw, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { api, errorMessage } from "@/lib/api";
import { MetricLabel } from "@/lib/metrics";

// K3 — 自動資料正確性檢查 (daily 11:00 + manual)
let _p = null, _at = 0;
const fetchStatus = (force) => {
  if (force || !_p || Date.now() - _at > 60000) { _at = Date.now(); _p = api.get("/data-accuracy/status").then((r) => r.data).catch(() => ({ flags: [] })); }
  return _p;
};

export function useAccuracyFlags(propertyId, widget) {
  const [flags, setFlags] = useState([]);
  useEffect(() => {
    let off = false;
    fetchStatus().then((d) => {
      if (off) return;
      setFlags((d.flags || []).filter((f) => (!widget || f.widget === widget) && (!propertyId || propertyId === "all" || f.property_id === propertyId)));
    });
    return () => { off = true; };
  }, [propertyId, widget]);
  return flags;
}

// plain-zh ⚠️ lines for a widget hover, e.g. 「此期間 CSV 與官方統計差 26%，建議重新匯入」
export const accuracyNotes = (flags) => flags.slice(0, 4).map((f) => `${f.message_zh} → ${f.fix_zh}`);

export function AccuracyBanner({ testid = "accuracy-banner" }) {
  const [d, setD] = useState(null);
  useEffect(() => { fetchStatus().then(setD); }, []);
  if (!d || !d.red_properties) return null;
  return (
    <div className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-800 flex items-center gap-2" data-testid={testid}>
      <AlertTriangle className="h-4 w-4 shrink-0" />
      <span className="flex-1">{d.red_properties} 個場域資料可能有誤</span>
      <Link to="/settings/data-health#accuracy" className="font-medium underline" data-testid={`${testid}-link`}>查看</Link>
    </div>
  );
}

const SEV = { red: "border-rose-200 bg-rose-50 text-rose-700", yellow: "border-amber-200 bg-amber-50 text-amber-800" };

export function DataAccuracySection() {
  const [d, setD] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { fetchStatus(true).then(setD); }, []);
  const run = async () => {
    setBusy(true);
    try { const r = await api.post("/data-accuracy/run"); _p = Promise.resolve(r.data); _at = Date.now(); setD(r.data); toast.success("檢查完成"); }
    catch (e) { toast.error(errorMessage(e, "檢查失敗")); } finally { setBusy(false); }
  };
  if (!d) return null;
  const groups = {};
  (d.flags || []).forEach((f) => { (groups[f.property_name || "全帳戶"] ||= []).push(f); });
  return (
    <Card className="bg-white border-slate-200" id="accuracy" data-testid="data-accuracy-section">
      <CardHeader className="py-3 flex-row items-center justify-between space-y-0 gap-2">
        <div>
          <CardTitle className="text-sm text-slate-900"><MetricLabel label="資料正確性" hintKey="__data_accuracy" testid="data-accuracy-info"
            extra={<div className="text-slate-600">每天 11:00 自動檢查近 30 天（好友 CSV vs 官方統計、即時通知漏接、CSV 缺日、Meta 花費、營收、對話分類積壓）。通過後問題會自動解除，歷史紀錄保留。門檻可在「異常與燈號規則」調整。</div>} /></CardTitle>
          <p className="text-[11px] text-slate-500 mt-0.5" data-testid="data-accuracy-summary">{(d.flags || []).filter((f) => f.severity === "red").length} 項可能有誤 · {(d.flags || []).filter((f) => f.severity !== "red").length} 項提醒 · 通過 {d.passed} 項</p>
          <p className="text-[11px] text-slate-400 mt-0.5" data-testid="data-accuracy-last-run">上次檢查：{d.last_run ? `${new Date(d.last_run.at).toLocaleString("zh-TW", { timeZone: "Asia/Taipei", hour12: false })}（${d.last_run.trigger === "manual" ? "手動" : "排程"}）` : "尚未檢查"}</p>
        </div>
        <Button size="sm" variant="outline" onClick={run} disabled={busy} data-testid="data-accuracy-run">
          {busy ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5 mr-1" />}立即檢查
        </Button>
      </CardHeader>
      <CardContent className="pt-0 space-y-3">
        {!d.flags?.length && <div className="text-xs text-emerald-700 flex items-center gap-1" data-testid="data-accuracy-ok"><ShieldCheck className="h-4 w-4" />目前沒有發現資料問題</div>}
        {Object.keys(groups).length > 0 && (
        <details className="rounded-lg border border-slate-200" open={(d.flags || []).some((f) => f.severity === "red")} data-testid="data-accuracy-details">
          <summary className="cursor-pointer px-3 py-1.5 text-xs text-slate-600">各場域明細（{Object.keys(groups).length} 個場域）</summary>
          <div className="p-2 space-y-2">
        {Object.entries(groups).map(([name, fs]) => (
          <details key={name} className="rounded-lg border border-slate-200" open={fs.some((f) => f.severity === "red")} data-testid={`data-accuracy-group-${fs[0].property_id || "account"}`}>
            <summary className="cursor-pointer px-3 py-1.5 text-xs flex items-center gap-2">
              <span className="font-semibold text-slate-800">{name}</span>
              <span className={`rounded px-1.5 text-[10px] ${fs.some((f) => f.severity === "red") ? "bg-rose-100 text-rose-800" : "bg-amber-100 text-amber-800"}`}>{fs.length} 項</span>
              <span className="truncate text-slate-500">{fs[0].message_zh}</span>
            </summary>
            <div className="px-3 pb-2 space-y-1">
            {fs.map((f, i) => (
              <div key={i} className={`rounded border px-2.5 py-1.5 text-xs flex flex-wrap items-center gap-x-3 gap-y-1 ${SEV[f.severity]}`} data-testid={`data-accuracy-flag-${f.check}-${f.property_id || "account"}`}>
                <span className="font-medium">{f.severity === "red" ? "可能有誤" : "提醒"}</span>
                <span className="flex-1 min-w-[240px] text-slate-800">{f.message_zh}</span>
                <span className="text-slate-600">建議：{f.fix_zh}</span>
                {f.fix_link && <Link to={f.fix_link} className="underline font-medium" data-testid={`data-accuracy-fix-${f.check}-${f.property_id || "account"}`}>一鍵前往修復</Link>}
              </div>
            ))}
            </div>
          </details>
        ))}
          </div>
        </details>
        )}
      </CardContent>
    </Card>
  );
}
